import React, { useCallback, useEffect, useState } from "react";
import DealerCardSetupModal from "components/DealerCardSetupModal";
import PaymentMethodDisplayCard from "components/PaymentMethodDisplayCard";
import useDealerPaymentMethods from "hooks/useDealerPaymentMethods";
import styles from "./PaymentMethodManager.module.css";

export default function PaymentMethodManager({ userId, title = "אמצעי תשלום", onSaved }) {
  const { methods, defaultMethodId, loading, error, fetchMethods, setDefault, removeMethod, addMethod } =
    useDealerPaymentMethods();
  const [modalOpen, setModalOpen] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    if (userId) fetchMethods(userId);
  }, [fetchMethods, userId]);

  useEffect(() => { load(); }, [load]);

  const handleCompleted = (paymentMethod) => {
    addMethod(paymentMethod);
    setModalOpen(false);
    onSaved?.({ has_payment_method: true, payment_method: paymentMethod });
    // רענון מהשרת לוודא עקביות
    fetchMethods(userId);
  };

  const handleSetDefault = async (method) => {
    setActionError("");
    setActionLoading(true);
    try {
      await setDefault(userId, method.id);
    } catch {
      setActionError("לא ניתן לשנות ברירת מחדל כרגע.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async (method) => {
    if (!window.confirm("האם להסיר את הכרטיס?")) return;
    setActionError("");
    setActionLoading(true);
    try {
      await removeMethod(userId, method.id);
    } catch {
      setActionError("לא ניתן להסיר את הכרטיס כרגע.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <section className={styles.manager} dir="rtl">
      <div className={styles.card}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>שמירת אמצעי תשלום</p>
          <h2>{title}</h2>
          <p>אמצעי התשלום נשמר בצורה מאובטחת ב-PayMe. לא מתבצע חיוב בשלב השמירה.</p>
        </header>

        {loading ? (
          <div className={styles.inlineLoading} aria-live="polite">
            <span className={styles.spinner} />
            <span>טוענים אמצעי תשלום...</span>
          </div>
        ) : methods.length > 0 ? (
          <div className={styles.methodsList}>
            {methods.map((m) => (
              <PaymentMethodDisplayCard
                key={m.id}
                paymentMethod={m}
                variant="profile"
                isDefault={Number(m.id) === Number(defaultMethodId)}
                onSetDefault={handleSetDefault}
                onRemove={handleRemove}
              />
            ))}
            <button
              type="button"
              className={styles.addButton}
              onClick={() => setModalOpen(true)}
              disabled={actionLoading}
            >
              + הוספת כרטיס נוסף
            </button>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon} aria-hidden="true"><span /></div>
            <h3>עדיין לא נשמר אמצעי תשלום</h3>
            <p>כאן אפשר להוסיף כרטיס בצורה מאובטחת. לא מתבצע חיוב בשלב זה.</p>
            <button type="button" className={styles.primaryButton} onClick={() => setModalOpen(true)}>
              הוספת אמצעי תשלום
            </button>
          </div>
        )}

        {(!!error || !!actionError) && (
          <div className={styles.pageError}>{actionError || error}</div>
        )}
      </div>

      <DealerCardSetupModal
        open={modalOpen}
        userId={userId}
        mode="profile"
        onClose={() => setModalOpen(false)}
        onCompleted={handleCompleted}
      />
    </section>
  );
}
